import { apiClient } from './apiClient';
import {
    BusinessDataFieldDto,
    CreateBusinessDataFieldDto,
    UpdateBusinessDataFieldDto,
    UserDataFieldDto,
    UpdateUserDataFieldDto
} from '../types/customUserDataFields';

class CustomUserDataFieldService {
    private userDataFieldsCache: UserDataFieldDto[] | null = null;
    private userDataFieldsPromise: Promise<UserDataFieldDto[]> | null = null;

    // Business Data Fields API
    async getBusinessDataFields(): Promise<BusinessDataFieldDto[]> {
        const response = await apiClient.get('/business-data-fields');
        return response.data;
    }

    async getBusinessDataField(id: string): Promise<BusinessDataFieldDto> {
        const response = await apiClient.get(`/business-data-fields/${id}`);
        return response.data;
    }

    async createBusinessDataField(dto: CreateBusinessDataFieldDto): Promise<BusinessDataFieldDto> {
        const response = await apiClient.post('/business-data-fields', dto);
        return response.data;
    }

    async updateBusinessDataField(id: string, dto: UpdateBusinessDataFieldDto): Promise<BusinessDataFieldDto> {
        const response = await apiClient.put(`/business-data-fields/${id}`, dto);
        return response.data;
    }

    async deleteBusinessDataField(id: string): Promise<void> {
        await apiClient.delete(`/business-data-fields/${id}`);
    }

    // User Data Fields API with caching
    async getCurrentUserFields(): Promise<UserDataFieldDto[]> {
        // Return cached data if available
        if (this.userDataFieldsCache !== null) {
            return this.userDataFieldsCache;
        }

        // If a request is already in flight, return that promise to avoid duplicate requests
        if (this.userDataFieldsPromise !== null) {
            return this.userDataFieldsPromise;
        }

        // Start a new request
        this.userDataFieldsPromise = apiClient.get('/user-data-fields')
            .then(response => {
                this.userDataFieldsCache = response.data;
                this.userDataFieldsPromise = null;
                return response.data;
            })
            .catch(error => {
                this.userDataFieldsPromise = null;
                throw error;
            });

        return this.userDataFieldsPromise;
    }

    async updateUserField(businessDataFieldId: string, dto: UpdateUserDataFieldDto): Promise<UserDataFieldDto> {
        const response = await apiClient.put(`/user-data-fields/${businessDataFieldId}`, dto);

        // Invalidate cache when updating
        this.clearCache();

        return response.data;
    }

    // Get user data fields for a specific user (e.g., assigned salesperson on a job)
    async getUserFieldsByUserId(userId: string): Promise<UserDataFieldDto[]> {
        const response = await apiClient.get(`/user-data-fields/users/${userId}`);
        return response.data;
    }

    // Clear the cache (useful after updates or logout)
    clearCache(): void {
        this.userDataFieldsCache = null;
        this.userDataFieldsPromise = null;
    }
}

export const customUserDataFieldService = new CustomUserDataFieldService();
